import { Breadcrumbs } from "components/breadcrumbs";
import { useOne, useParse } from "@refinedev/core";
import { FullScreenLoading } from "components/fullScreenLoading";
import { Link } from "react-router-dom";
import Button from "components/button";
import { PencilIcon } from "@heroicons/react/outline";
import {
  formatNumber,
  getEventAddonQuantityModeLabel,
  getEventAddonScopeLabel,
  getEventAddonTypeLabel,
  isHaveActionPermission,
} from "utills/helpers";
import { Label } from "components/label";
import { Tag } from "components/tag";
import { capitalize } from "lodash";
import { HistoryLogButton } from "components/historyLogButton";

interface IEventAddonDetail {
  id: string;
  event_id: string;
  name?: string;
  name_en: string;
  name_vi: string;
  desc?: string;
  desc_en: string;
  desc_vi: string;
  base_price: number;
  stock_total: number | null;
  stock_remaining: number | null;
  status: string;
  type: string;
  scope: string;
  ticket_ids: string[];
  position: number;
  required: boolean;
  is_sold_out: boolean;
  qty_mode: string;
  qty_min: number;
  qty_max: number;
  has_note: boolean;
  note_label_vi: string;
  note_label_en: string;
  note_placeholder_vi: string;
  note_placeholder_en: string;
  note_required: boolean;
  low_stock_threshold: number;
  variants: IAddonVariant[];
  created_by: string;
}

interface IAddonVariant {
  id?: string;
  label?: string;
  label_vi: string;
  label_en: string;
  price: number;
  stock: number;
  qty_min: number;
  qty_max: number;
  position: number;
}

const DetailField = ({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: React.ReactNode;
}) => (
  <div>
    <Label id={id} name={label} />
    <div id={id} className="text-sm text-gray-900">
      {children === null || children === undefined || children === "" ? "-" : children}
    </div>
  </div>
);

export const EventAddonDetail: React.FC = () => {
  const parse = useParse();
  const { params } = parse();

  const { data, isFetching, isPreviousData, isLoading } =
    useOne<IEventAddonDetail>({
      resource: "ws-event-addons",
      id: params?.id,
      meta: {
        resource: "data",
      },
      queryOptions: {
        enabled: !!params?.id,
      } as any,
    });

  const addonDetailData = data?.data
    ? {
        ...data.data,
        name_vi: data.data.name_vi || data.data.name || "",
        name_en: data.data.name_en || data.data.name || "",
        desc_vi: data.data.desc_vi || data.data.desc || "",
        desc_en: data.data.desc_en || data.data.desc || "",
        variants: (data.data.variants ?? []).map((variant, index) => ({
          ...variant,
          label_vi: variant.label_vi || variant.label || "",
          label_en: variant.label_en || variant.label || "",
          position: variant.position ?? index + 1,
        })),
      }
    : null;

  if (isLoading) {
    return <FullScreenLoading />;
  }

  return (
    <div className="wrapper">
      <Breadcrumbs
        customPageLabel="Event Addon Detail"
        additionalLabel={
          addonDetailData ? (
            <div className="flex items-center gap-2">
              <HistoryLogButton
                resource={`ws-event-addons/${addonDetailData.id}/history`}
              />
              {isHaveActionPermission("addons", "events", "edit") && (
                <Link to={`/events/addons/${params?.id}/edit`}>
                  <Button className="h-10 w-10 sm:w-auto px-0 sm:px-3.5 py-0 sm:py-2.5">
                    <PencilIcon className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:block">Edit</span>
                  </Button>
                </Link>
              )}
            </div>
          ) : null
        }
      />
      {addonDetailData ? (
        <div className="box-container">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-semibold">
              {addonDetailData.name_en}
            </h1>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <DetailField id="name_vi" label="Name (vi)">
              {addonDetailData.name_vi}
            </DetailField>
            <DetailField id="name_en" label="Name (en)">
              {addonDetailData.name_en}
            </DetailField>
            <DetailField id="desc_vi" label="Description (vi)">
              {addonDetailData.desc_vi}
            </DetailField>
            <DetailField id="desc_en" label="Description (en)">
              {addonDetailData.desc_en}
            </DetailField>
            <DetailField id="base_price" label="Base Price">
              {formatNumber(addonDetailData.base_price)}
            </DetailField>
            <DetailField id="stock_total" label="Stock Total">
              {addonDetailData.stock_total ?? "Unlimited"}
            </DetailField>
            <DetailField id="stock_remaining" label="Stock Remaining">
              {addonDetailData.stock_remaining ?? "Unlimited"}
            </DetailField>
            <DetailField id="type" label="Type">
              {getEventAddonTypeLabel(addonDetailData.type)}
            </DetailField>
            <DetailField id="scope" label="Scope">
              {getEventAddonScopeLabel(addonDetailData.scope)}
            </DetailField>
            <div>
              <Label id="status" name="Status" />
              <Tag
                text={capitalize(addonDetailData.status)}
                type={addonDetailData.status === "active" ? "green" : "pink"}
              />
            </div>
            <DetailField id="qty_mode" label="Quantity Mode">
              {getEventAddonQuantityModeLabel(addonDetailData.qty_mode)}
            </DetailField>
            <DetailField id="qty_range" label="Quantity Range">
              {addonDetailData.qty_min} - {addonDetailData.qty_max ?? "Unlimited"}
            </DetailField>
            <DetailField id="low_stock_threshold" label="Low Stock Threshold">
              {addonDetailData.low_stock_threshold}
            </DetailField>
            <DetailField id="required" label="Required">
              {addonDetailData.required ? "Yes" : "No"}
            </DetailField>
            <DetailField id="is_sold_out" label="Sold Out">
              {addonDetailData.is_sold_out ? "Yes" : "No"}
            </DetailField>
            <DetailField id="has_note" label="Has Note">
              {addonDetailData.has_note ? "Yes" : "No"}
            </DetailField>
            <DetailField id="note_required" label="Note Required">
              {addonDetailData.note_required ? "Yes" : "No"}
            </DetailField>
          </div>

          {addonDetailData.variants && addonDetailData.variants.length > 0 && (
            <div className="mt-8">
              <h2 className="text-xl font-semibold mb-4">Variants</h2>
              <div className="flow-root">
                <div className="relative inline-block w-full align-middle box-border overflow-x-auto">
                  <table className="w-full divide-y divide-gray-300 bg-magnolia-500 rounded-md">
                    <thead>
                      <tr>
                        <th
                          scope="col"
                          className="th py-3.5 pl-4 pr-3 sm:pl-6 lg:pl-8"
                        >
                          Label VI
                        </th>
                        <th scope="col" className="th">
                          Label EN
                        </th>
                        <th scope="col" className="th">
                          Price
                        </th>
                        <th scope="col" className="th">
                          Stock
                        </th>
                        <th scope="col" className="th">
                          Qty Range
                        </th>
                        <th scope="col" className="th">
                          Position
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {addonDetailData.variants.map((variant, idx) => (
                        <tr key={idx}>
                          <td className="td py-4 pl-4 pr-3 sm:pl-6 lg:pl-8">
                            {variant.label_vi}
                          </td>
                          <td className="td">{variant.label_en}</td>
                          <td className="td">{formatNumber(variant.price)}</td>
                          <td className="td">{variant.stock}</td>
                          <td className="td">
                            {variant.qty_min} - {variant.qty_max}
                          </td>
                          <td className="td">{variant.position}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <></>
      )}
      {isFetching && isPreviousData ? <FullScreenLoading /> : <></>}
    </div>
  );
};
